// src/pages/TicketsList.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import Spinner from "react-js-loader";

const formatIngreso = value => {
  if (!value) return "—";
  const clean = String(value).trim().replace("T", " ");
  const [fecha = "", hora = ""] = clean.split(/\s+/);
  const partes = fecha.replaceAll("-", "/").split("/");
  const fechaVisible = partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : fecha;
  return `${fechaVisible}${hora ? ` ${hora.slice(0, 5)}` : ""}`;
};

export default function TicketsList() {
  const [tickets, setTickets] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState("");
  const [selectedFuncion, setSelectedFuncion] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmPopup, setConfirmPopup] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const isUsed = val => Boolean(val && val !== "false" && val !== "0");

  const [filters, setFilters] = useState({
    dni: "",
    first_name: "",
    last_name: "",
    functionId: "",
    idEvento: "",
    cantidad: "",
    tanda: "",
    estado: "",
    used: "",
  });

  // --------------------------------------------------------------------
  // LOADER OVERLAY
  // --------------------------------------------------------------------
  const loaderOverlay = loading && (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <Spinner
        type="bubble-top"
        bgColor={"#dfa66b"}
        color={"#dfa66b"}
        size={90}
      />
    </div>
  );

  // --------------------------------------------------------------------
  // Cargar eventos
  // --------------------------------------------------------------------
  const cargarEventos = async () => {
    try {
      const loginType = localStorage.getItem("loginType");
      const res = await axios.post(
        "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod/getEventsSensoria",
        { loginType },
      );
      setEventos(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarEventos();
  }, []);

  // --------------------------------------------------------------------
  // Cargar tickets
  // --------------------------------------------------------------------
  const cargarTickets = async () => {
    if (!selectedEvento) return alert("Elegí un evento");
    setTickets([]);
    setFiltered([]);
    setLoading(true);
    try {
      const res = await axios.post(
        "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod/getTicketsByEvent",
        { idEvento: selectedEvento, funcionId: selectedFuncion || null },
      );
      setTickets(res.data);
      setFiltered(res.data);
    } finally {
      setLoading(false);
    }
  };

  const cargarTodos = async () => {
    setTickets([]);
    setFiltered([]);
    setSelectedEvento("");
    setSelectedFuncion("");
    setLoading(true);
    try {
      const res = await axios.post(
        "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod/getTicketsByEvent",
      );
      setTickets(res.data);
      setFiltered(res.data);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------
  // FILTROS
  // --------------------------------------------------------------------
  const applyFilters = () => {
    let f = [...tickets];
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        f = f.filter((row) => {
          if (key === "used") {
            if (filters.used.toLowerCase() === "si") return isUsed(row.used);
            if (filters.used.toLowerCase() === "no") return !isUsed(row.used);
          }
          return String(row[key])
            .toLowerCase()
            .includes(filters[key].toLowerCase());
        });
      }
    });
    setFiltered(f);
  };

  // --------------------------------------------------------------------
  // ORDENAR
  // --------------------------------------------------------------------
  const sortColumn = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    const sorted = [...filtered].sort((a, b) => {
      let A = a[key] ?? "";
      let B = b[key] ?? "";
      if (key === "used") {
        A = isUsed(A) ? 1 : 0;
        B = isUsed(B) ? 1 : 0;
      } else {
        if (typeof A === "string") A = A.toLowerCase();
        if (typeof B === "string") B = B.toLowerCase();
      }
      if (A < B) return direction === "asc" ? -1 : 1;
      if (A > B) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setFiltered(sorted);
  };

  // --------------------------------------------------------------------
  // ELIMINAR
  // --------------------------------------------------------------------
  const eliminarTicketsPorDNI = async (dni) => {
    setLoading(true);
    try {
      await axios.post(
        "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod/delete_Sensoria_Entradas",
        { dni, idEvento: selectedEvento, pdf_url: confirmPopup.pdf_url },
      );
      cargarTickets();
    } finally {
      setConfirmPopup(null);
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------
  // STYLES
  // --------------------------------------------------------------------
  const thStyle = {
    padding: 8,
    cursor: "pointer",
    background: "#222",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 2,
  };

  const filterInput = (key, placeholder = "") => (
    <input
      value={filters[key]}
      placeholder={placeholder}
      onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
      onKeyUp={applyFilters}
      style={{
        width: "90%",
        padding: 4,
        marginTop: 4,
        background: "#333",
        color: "#fff",
        border: "1px solid #444",
        borderRadius: 4,
      }}
    />
  );

  // --------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------
  return (
    <div style={{ padding: 20, color: "#fff" }}>
      {loaderOverlay}

      <h2>Entradas del evento</h2>

      {/* SELECT EVENTO + FUNCIÓN */}
      <div style={{ margin: "20px 0" }}>
        <label style={{ marginRight: 10 }}>Evento:</label>
        <select
          value={selectedEvento}
          onChange={(e) => {
            setSelectedEvento(e.target.value);
            setSelectedFuncion("");
            setTickets([]);
            setFiltered([]);
          }}
          style={{
            padding: 10,
            borderRadius: 6,
            background: "#222",
            color: "#fff",
            marginRight: 20,
          }}
        >
          <option value="">-- Elegir Evento --</option>
          {eventos.map((ev) => (
            <option key={ev.idEvento} value={ev.idEvento}>
              {ev.name}
            </option>
          ))}
        </select>

        <label style={{ marginRight: 10 }}>Función:</label>
        <select
          value={selectedFuncion}
          onChange={(e) => setSelectedFuncion(e.target.value)}
          disabled={!selectedEvento}
          style={{
            padding: 10,
            borderRadius: 6,
            background: "#222",
            color: "#fff",
          }}
        >
          <option value="">-- Elegir Función --</option>
          {selectedEvento &&
            eventos
              .find((ev) => ev.idEvento === selectedEvento)
              ?.funciones?.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
        </select>
      </div>

      {/* BOTONES */}
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <button
          onClick={cargarTickets}
          style={{
            padding: "10px 20px",
            background: "#dfa66b",
            color: "#000",
            borderRadius: 6,
            fontWeight: "bold",
          }}
        >
          Actualizar
        </button>
        <button
          onClick={cargarTodos}
          style={{
            padding: "10px 20px",
            background: "#444",
            color: "#fff",
            borderRadius: 6,
            fontWeight: "bold",
          }}
        >
          Ver todos
        </button>
      </div>

      <p style={{ marginBottom: 20 }}>
        Cantidad de entradas en la vista: <b>{filtered.length}</b>
      </p>

      {/* TABLA */}
      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          border: "1px solid #333",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginBottom: 80,
          }}
        >
          <thead>
            <tr>
              <th style={thStyle}>Acción</th>
              <th style={thStyle}>PDF</th>
              <th style={thStyle} onClick={() => sortColumn("createdBy")}>
                createdBy {filterInput("createdBy")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("dni")}>
                DNI {filterInput("dni")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("first_name")}>
                Nombre {filterInput("first_name")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("last_name")}>
                Apellido {filterInput("last_name")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("functionId")}>
                Función {filterInput("functionId")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("cantidadTotal")}>
                Nº Ticket {filterInput("cantidadTotal")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("tanda")}>
                Tanda {filterInput("tanda")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("tipoPago")}>
                Estado {filterInput("tipoPago")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("used")}>
                Usado {filterInput("used", "si / no")}
              </th>
              <th style={thStyle} onClick={() => sortColumn("usedAt")}>
                Fecha y hora
              </th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((t, i) => {
              const usado = isUsed(t.used);
              return (
                <tr
                  key={i}
                  style={{ background: i % 2 === 0 ? "#111" : "#181818" }}
                >
                  {/* Botón ELIMINAR — deshabilitado si fue usado */}
                  <td style={{ padding: 8 }}>
                    <button
                      disabled={usado}
                      title={
                        usado
                          ? "No se puede eliminar: entrada ya utilizada"
                          : "Eliminar"
                      }
                      style={{
                        background: usado ? "#555" : "#d50000",
                        color: usado ? "#999" : "#fff",
                        padding: "6px 12px",
                        borderRadius: 6,
                        cursor: usado ? "not-allowed" : "pointer",
                        fontWeight: "bold",
                        border: "none",
                      }}
                      onClick={() => {
                        if (usado) return;
                        setConfirmPopup({
                          dni: t.dni,
                          nombre: t.first_name,
                          apellido: t.last_name,
                          pdf_url: t.pdf_url,
                        });
                      }}
                    >
                      🗑️
                    </button>
                  </td>

                  {/* PDF */}
                  <td style={{ padding: 8, textAlign: "center" }}>
                    {t.pdf_url ? (
                      <a
                        href={t.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          padding: "5px 10px",
                          background: "#dfa66b",
                          color: "#333",
                          borderRadius: 6,
                          fontWeight: "bold",
                          fontSize: "0.85em",
                          textDecoration: "none",
                        }}
                      >
                        ⬇ PDF
                      </a>
                    ) : (
                      <span style={{ color: "#555" }}>—</span>
                    )}
                  </td>

                  <td style={{ padding: 8 }}>{t.createdBy}</td>
                  <td style={{ padding: 8 }}>{t.dni}</td>
                  <td style={{ padding: 8 }}>{t.first_name}</td>
                  <td style={{ padding: 8 }}>{t.last_name}</td>
                  <td style={{ padding: 8 }}>{t.functionId}</td>
                  <td style={{ padding: 8 }}>
                    {t.numeroTicket} / {t.cantidadTotal}
                  </td>
                  <td style={{ padding: 8 }}>{t.tanda}</td>
                  <td style={{ padding: 8 }}>
                    {t.tipoPago === "EFECTIVO"
                      ? "🟢 EFECTIVO"
                      : "🔴 " + t.tipoPago}
                  </td>
                  <td style={{ padding: 8 }}>{usado ? "🟢 Sí" : "⚪ No"}</td>
                  <td
                    style={{
                      padding: 8,
                      fontSize: "0.82em",
                      color: usado ? "#aaffaa" : "#555",
                    }}
                  >
                    {formatIngreso(t.usedAt || t.used)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* POPUP CONFIRMACIÓN */}
      {confirmPopup && (
        <div
          onClick={() => setConfirmPopup(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(3px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
            cursor: "pointer",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#222",
              padding: 30,
              width: "90%",
              maxWidth: 420,
              borderRadius: 12,
              textAlign: "center",
              color: "#fff",
              boxShadow: "0 0 20px rgba(0,0,0,0.5)",
            }}
          >
            <h3 style={{ marginBottom: 20 }}>
              ¿Eliminar TODAS las entradas del DNI:
              <br />
              <span style={{ color: "#dfa66b" }}>
                {confirmPopup.dni} – {confirmPopup.nombre}{" "}
                {confirmPopup.apellido}
              </span>
              ?
            </h3>

            <div style={{ display: "flex", gap: 20, justifyContent: "center" }}>
              <button
                style={{
                  padding: "10px 20px",
                  background: "#d50000",
                  color: "#fff",
                  borderRadius: 6,
                  fontWeight: "bold",
                }}
                onClick={() => eliminarTicketsPorDNI(confirmPopup.dni)}
              >
                Sí, eliminar
              </button>
              <button
                style={{
                  padding: "10px 20px",
                  background: "#444",
                  color: "#fff",
                  borderRadius: 6,
                }}
                onClick={() => setConfirmPopup(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
